import { useCallback, useMemo, useRef, type ReactNode } from 'react';
import { useNostr } from '@nostrify/react';
import { EventStatsBatchContext } from '@/contexts/EventStatsContext';
import { DITTO_RELAY, DITTO_STATS_PUBKEY, parseEventStats, type EventStats } from '@/lib/ditto';

type Resolver = (data: EventStats | null) => void;

const BATCH_WINDOW_MS = 50;
const MAX_IDS_PER_QUERY = 100;

/**
 * Coalesces many concurrent `useEventStats` calls into batched
 * `{ kinds: [30383], authors: [DITTO_STATS_PUBKEY], '#d': [...] }` queries
 * (one round-trip per ~100 event ids) instead of four count queries per note.
 * Targets the Ditto relay directly so it works regardless of the user's relay
 * selection. Events with no Ditto assertion resolve to `null` so callers can
 * fall back to client-side counting.
 */
export function EventStatsProvider({ children }: { children: ReactNode }) {
  const { nostr } = useNostr();
  const pending = useRef(new Map<string, Resolver[]>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(async () => {
    timer.current = null;
    const batch = pending.current;
    pending.current = new Map();
    const ids = [...batch.keys()];
    if (ids.length === 0) return;

    const resolveAll = (results: Map<string, EventStats>) => {
      for (const [id, resolvers] of batch) {
        const data = results.get(id) ?? null;
        for (const resolve of resolvers) resolve(data);
      }
    };

    try {
      const results = new Map<string, EventStats>();
      for (let i = 0; i < ids.length; i += MAX_IDS_PER_QUERY) {
        const chunk = ids.slice(i, i + MAX_IDS_PER_QUERY);
        const events = await nostr.query(
          [{ kinds: [30383], authors: [DITTO_STATS_PUBKEY], '#d': chunk }],
          { relays: [DITTO_RELAY], signal: AbortSignal.timeout(4000) },
        );
        for (const event of events) {
          const id = event.tags.find(([t]) => t === 'd')?.[1];
          if (id) results.set(id, parseEventStats(event));
        }
      }
      resolveAll(results);
    } catch {
      resolveAll(new Map());
    }
  }, [nostr]);

  const load = useCallback((eventId: string): Promise<EventStats | null> => {
    return new Promise<EventStats | null>((resolve) => {
      const resolvers = pending.current.get(eventId);
      if (resolvers) {
        resolvers.push(resolve);
      } else {
        pending.current.set(eventId, [resolve]);
      }
      if (timer.current === null) {
        timer.current = setTimeout(flush, BATCH_WINDOW_MS);
      }
    });
  }, [flush]);

  const value = useMemo(() => ({ load }), [load]);

  return <EventStatsBatchContext.Provider value={value}>{children}</EventStatsBatchContext.Provider>;
}
